<script lang="ts" setup>
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import {
    faPlus,
    faSignIn,
    faSignOut,
    faSpinnerThird,
    faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import type { PartnerTenant } from "~/models/User";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { formatTimeAgo } from "@vueuse/core";

const open = defineModel<boolean>("open", { required: false, default: false });

const api = useApi();
const {
    user,
    isPartnerManager,
    isSuperAdmin,
    refresh: userRefresh,
} = useUser();

const selectedTenant = ref<PartnerTenant | null>(null);
const search = ref<string>("");
const isCreatingAccount = ref<boolean>(false);

const searchDebounced = refDebounced(search, 500);

const { data, items, error, status, page, query, total, refresh } =
    api.partners.tenants.list({
        show: 5,
        query: {
            per_page: 5,
            sorts: "-last_signed_in_at",
            search: undefined,
        },
        lazy: true,
    });

watch(searchDebounced, async (value) => {
    query.value.search = value;
});

watch(status, (to) => {
    if (to === "pending") {
        selectedTenant.value = null;
    }
});

defineShortcuts({
    H: {
        usingInput: false,
        handler() {
            open.value = !open.value;
        },
    },
});

function reset() {
    query.value = {
        sorts: "-updated_at",
        search: undefined,
    };
}

function onCreated(tenant: PartnerTenant) {
    reset();
    open.value = true;
    console.log(tenant);
}

function onOpenCreate() {
    isCreatingAccount.value = true;
    open.value = false;
}

function onSwitch() {
    open.value = true;
    reset();
}

const loggingIn = ref<boolean>(false);
const loggingOut = ref<boolean>(false);

async function onLogin(tenant: PartnerTenant) {
    if (tenant) {
        loggingIn.value = true;
        try {
            await api.partners.tenants.login(tenant.id);
            userRefresh();
            refreshNuxtData();
            open.value = false;

            useToast().add({
                title: "Logged in to " + tenant.tenant.name,
            });
        } catch (error) {
            console.error(error);
        } finally {
            loggingIn.value = false;
        }
    }
}

async function onLogout() {
    if (user.value?.current_partner_tenant) {
        loggingOut.value = true;
        try {
            const tenantName = user.value.current_partner_tenant?.tenant.name;

            await api.partners.tenants.logout();
            userRefresh();
            refreshNuxtData();
            open.value = false;

            useToast().add({
                title: "Logged out of " + tenantName,
            });
        } catch (error) {
            console.error(error);
        } finally {
            loggingOut.value = false;
        }
    }
}
</script>

<template>
    <UModal v-model="open">
        <UCard>
            <template #header>
                <div class="flex items-center gap-4">
                    <h3 class="font-medium text-lg font-display">Accounts</h3>
                    <UButton
                        v-if="isPartnerManager || isSuperAdmin"
                        class="px-4 py-2 focus-visible:ring-0"
                        color="hd-primary"
                        size="sm"
                        @click="onOpenCreate"
                    >
                        <template #leading>
                            <FontAwesomeIcon :icon="faPlus" size="lg" />
                        </template>
                        <span>New Account</span>
                    </UButton>
                    <UButton
                        class="ml-auto justify-self-end"
                        color="gray"
                        square
                        variant="ghost"
                        @click="open = false"
                    >
                        <FontAwesomeIcon
                            :icon="faTimes"
                            fixed-width
                            size="lg"
                        />
                    </UButton>
                </div>
            </template>
            <UAlert
                v-if="error"
                :description="error.data.statusText"
                :title="error.data.statusCode"
                color="red"
                icon="i-heroicons-exclamation-triangle"
            />
            <div v-else class="flex flex-col gap-4">
                <UInput
                    v-model="search"
                    placeholder="Search accounts..."
                    size="md"
                >
                    <template #leading>
                        <FontAwesomeIcon :icon="faSearch" class="opacity-50" />
                    </template>
                </UInput>
                <HdLoader :status="status">
                    <ul v-if="data" class="flex flex-col gap-2">
                        <li v-for="item in items">
                            <UButton
                                :class="{
                                    'ring-2 ring-primary-500':
                                        selectedTenant?.id === item.id,
                                }"
                                block
                                class="group justify-start text-left active:scale-100"
                                @click="onLogin(item)"
                                color="white"
                                size="lg"
                            >
                                <template #leading>
                                    <UIAvatar
                                        :alt="item.tenant.name"
                                        :src="item.tenant.logo"
                                        auto-fallback
                                        class="rounded-full"
                                        size="md"
                                    />
                                </template>
                                <div class="font-normal flex flex-col flex-1">
                                    <span class="font-medium">{{
                                        item.tenant.name
                                    }}</span>
                                    <span class="text-xs text-gray-500">
                                        Last signed in
                                        {{
                                            formatTimeAgo(
                                                new Date(
                                                    item.last_signed_in_at,
                                                ),
                                            )
                                        }}
                                    </span>
                                </div>
                                <template #trailing>
                                    <div class="flex items-center">
                                        <UTooltip
                                            v-for="(
                                                user, idx
                                            ) in item.current_users ?? []"
                                            :key="user.id"
                                            :text="user.name"
                                            :popper="{ placement: 'top' }"
                                            :class="{
                                                '-ml-3': idx > 0
                                            }"
                                        >
                                            <UIAvatar
                                                :alt="user.name"
                                                :src="user.avatar"
                                                auto-fallback
                                                class="rounded-full"
                                                size="xs"
                                            />
                                        </UTooltip>
                                    </div>
                                    <FontAwesomeIcon class="opacity-0 group-hover:opacity-100 transition-opacity duration-150" :icon="faSignIn" />
                                </template>
                            </UButton>
                        </li>
                        <li v-if="items.length === 0">
                            <UAlert title="No results found" />
                        </li>
                        <li
                            v-else
                            v-if="total > 5"
                            class="flex justify-center mt-2"
                        >
                            <AppListPaginationFooter
                                v-model:page="page"
                                :options="[]"
                                :show="5"
                                :total="total"
                                centered
                                hide-totals
                                label="accounts"
                            />
                        </li>
                    </ul>
                    <UButton
                        v-if="
                            selectedTenant &&
                            selectedTenant.id !==
                                user?.current_partner_tenant?.id
                        "
                        block
                        size="md"
                        @click="onLogin"
                    >
                        <template #leading>
                            <FontAwesomeIcon
                                :icon="faSpinnerThird"
                                spin
                                v-if="loggingIn"
                            />
                        </template>
                        Login to {{ selectedTenant.tenant.name }}
                        <template #trailing>
                            <FontAwesomeIcon :icon="faSignIn" />
                        </template>
                    </UButton>
                    <UButton
                        v-if="user?.current_partner_tenant"
                        block
                        size="md"
                        @click="onLogout"
                        color="white"
                    >
                        <template #leading>
                            <FontAwesomeIcon
                                :icon="faSpinnerThird"
                                spin
                                v-if="loggingOut"
                            />
                        </template>
                        Sign out of
                        {{ user.current_partner_tenant.tenant.name }}
                        <template #trailing>
                            <FontAwesomeIcon :icon="faSignOut" />
                        </template>
                    </UButton>
                    <hr />
                    <p class="text-center text-gray-400 text-sm">
                        Access to partner connected accounts mirrors your access
                        level in your primary partner account. You can easily
                        return to your primary account using the account
                        switcher.
                    </p>
                </HdLoader>
            </div>
        </UCard>
    </UModal>
    <UIModalCreateAccount
        v-model:open="isCreatingAccount"
        :create-account-modal="onOpenCreate"
        :switch-account-modal="onSwitch"
        partner
        @partner-tenant-created="onCreated"
    />
</template>

<style scoped></style>
